// frontend/src/modules/auditoria/pages/Auditoria.js
import React, { useState, useEffect } from 'react';
import { FaSearch, FaFilter, FaTimes, FaFileAlt, FaUser, FaBuilding, FaEye, FaDownload } from 'react-icons/fa';

function Auditoria({ usuario }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtros, setFiltros] = useState({
    acao: '',
    entidade: '',
    dataInicio: '',
    dataFim: ''
  });
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const empresaId = usuario?.empresa_id || 1;
  const token = localStorage.getItem('token');

  const fetchAuditoria = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filtros.acao) params.append('acao', filtros.acao);
      if (filtros.entidade) params.append('entidade', filtros.entidade);
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);
      params.append('page', pagina);
      params.append('limit', 20);

      const response = await fetch(
        `/api/auditoria/empresa/${empresaId}?${params.toString()}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao buscar auditoria');
      }

      const data = await response.json();
      setLogs(data.logs || []);
      setTotalPaginas(data.pagination?.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditoria();
  }, [empresaId, pagina, filtros]);

  const handleFiltroChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
    setPagina(1);
  };

  const limparFiltros = () => {
    setFiltros({ acao: '', entidade: '', dataInicio: '', dataFim: '' });
    setPagina(1);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  const getAcaoColor = (acao) => {
    const colors = {
      'CREATE': '#28a745',
      'UPDATE': '#007bff',
      'DELETE': '#dc3545',
      'LOGIN': '#17a2b8',
      'LOGOUT': '#6c757d',
      'VIEW': '#ffc107',
      'EXPORT': '#fd7e14'
    };
    return colors[acao] || '#6c757d';
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>🔄 Carregando logs de auditoria...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ margin: 0 }}>📋 Auditoria</h2>
          <p style={{ color: '#6c757d', margin: '5px 0 0 0' }}>
            Histórico de todas as ações realizadas no sistema
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            style={{
              padding: '8px 16px',
              backgroundColor: mostrarFiltros ? '#dc3545' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            {mostrarFiltros ? <FaTimes /> : <FaFilter />} 
            {mostrarFiltros ? 'Fechar' : 'Filtros'}
          </button>
          <button
            onClick={fetchAuditoria}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            <FaSearch /> Atualizar
          </button>
        </div>
      </div>

      {error && (
        <div style={{ backgroundColor: '#ffebee', padding: '10px', borderRadius: '4px', color: '#c62828', marginBottom: '15px' }}>
          ❌ {error}
        </div>
      )}

      {/* Filtros */}
      {mostrarFiltros && (
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
            <div>
              <label>Ação</label>
              <select
                name="acao"
                value={filtros.acao}
                onChange={handleFiltroChange}
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">Todas</option>
                <option value="CREATE">Criar</option>
                <option value="UPDATE">Atualizar</option>
                <option value="DELETE">Excluir</option>
                <option value="LOGIN">Login</option>
                <option value="LOGOUT">Logout</option>
                <option value="VIEW">Visualizar</option>
                <option value="EXPORT">Exportar</option>
              </select>
            </div>
            <div>
              <label>Entidade</label>
              <select
                name="entidade"
                value={filtros.entidade}
                onChange={handleFiltroChange}
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">Todas</option>
                <option value="Empresa">Empresa</option>
                <option value="Usuario">Usuário</option>
                <option value="Lancamento">Lançamento</option>
                <option value="GuiaPagamento">Guia</option>
                <option value="NotaFiscal">Nota Fiscal</option>
                <option value="Funcionario">Funcionário</option>
                <option value="Rescisao">Rescisão</option>
                <option value="FolhaPagamento">Folha</option>
              </select>
            </div>
            <div>
              <label>Data Início</label>
              <input
                type="date"
                name="dataInicio"
                value={filtros.dataInicio}
                onChange={handleFiltroChange}
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label>Data Fim</label>
              <input
                type="date"
                name="dataFim"
                value={filtros.dataFim}
                onChange={handleFiltroChange}
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>
          <button
            onClick={limparFiltros}
            style={{
              marginTop: '15px',
              padding: '8px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Limpar Filtros
          </button>
        </div>
      )}

      {/* Tabela de Logs */}
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        {logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>📭</div>
            <p style={{ color: '#6c757d' }}>Nenhum log de auditoria encontrado</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Data/Hora</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Usuário</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Ação</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Entidade</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Descrição</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '10px' }}>{formatDate(log.criado_em)}</td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaUser style={{ color: '#6c757d' }} />
                      {log.usuario?.nome_completo || 'Sistema'}
                    </div>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: getAcaoColor(log.acao) + '20',
                      color: getAcaoColor(log.acao),
                      fontWeight: 'bold',
                      fontSize: '12px'
                    }}>
                      {log.acao}
                    </span>
                  </td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaFileAlt style={{ color: '#6c757d' }} />
                      {log.entidade}
                    </div>
                  </td>
                  <td style={{ padding: '10px' }}>
                    {log.descricao || `${log.acao} em ${log.entidade}`}
                    {log.entidade_id && <span style={{ color: '#6c757d', fontSize: '12px' }}> (ID: {log.entidade_id})</span>}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: log.sucesso ? '#d4edda' : '#f8d7da',
                      color: log.sucesso ? '#155724' : '#721c24'
                    }}>
                      {log.sucesso ? '✅ Sucesso' : '❌ Falha'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Paginação */}
        {totalPaginas > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
            <button
              onClick={() => setPagina(p => Math.max(1, p - 1))}
              disabled={pagina === 1}
              style={{
                padding: '8px 16px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                background: 'white',
                cursor: pagina === 1 ? 'not-allowed' : 'pointer',
                opacity: pagina === 1 ? 0.5 : 1
              }}
            >
              ◀ Anterior
            </button>
            <span style={{ padding: '8px 16px' }}>
              Página {pagina} de {totalPaginas}
            </span>
            <button
              onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
              disabled={pagina === totalPaginas}
              style={{
                padding: '8px 16px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                background: 'white',
                cursor: pagina === totalPaginas ? 'not-allowed' : 'pointer',
                opacity: pagina === totalPaginas ? 0.5 : 1
              }}
            >
              Próximo ▶
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Auditoria;